/**
 * Visit の編集後にレシートの紐づけを画面へ反映する
 *
 * Visit の時間や場所を変えると、サーバー側でレシートの visit_id が付け直される。
 * 画面のレシートは Visit とは別にキャッシュしているため、Visit を編集したら取り直して紐づけを合わせる。
 */

import { type QueryClient, type QueryKey, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { getReceipts } from '@/api/services/receipt'
import { receiptKeys } from '@/pages/receipts/hooks/use-receipts'
import { useOverlandStore } from '@/store/useOverlandStore'

import { DAY_DATA_QUERY_PREFIX, type DayData } from './day-data'

/** 日別データ（無限タイムライン／カレンダー）のうち、レシートだけを取り直して差し替える */
const refreshDayDataReceipts = async (queryClient: QueryClient, queryKey: QueryKey): Promise<void> => {
  const data = queryClient.getQueryData<DayData>(queryKey)
  if (!data) return

  try {
    const receipts = await getReceipts({ purchased_date: data.date })
    queryClient.setQueryData<DayData>(queryKey, (current) => (current ? { ...current, receipts } : current))
  } catch {
    // 取り直せなくても次回の読み込みで反映されるので諦める
  }
}

export const useVisitReceiptSync = (): void => {
  const queryClient = useQueryClient()
  const visitMutationVersion = useOverlandStore((state) => state.visitMutationVersion)
  // マウント時点の値では取り直さない（編集が起きたときだけ反応する）
  const handledVersionRef = useRef(visitMutationVersion)

  useEffect(() => {
    if (visitMutationVersion === handledVersionRef.current) return
    handledVersionRef.current = visitMutationVersion

    void queryClient.invalidateQueries({ queryKey: receiptKeys.all })

    const activeDayQueries = queryClient.getQueryCache().findAll({ queryKey: [DAY_DATA_QUERY_PREFIX], type: 'active' })
    for (const query of activeDayQueries) {
      void refreshDayDataReceipts(queryClient, query.queryKey)
    }
  }, [visitMutationVersion, queryClient])
}
